import React, { useCallback, useRef } from 'react';

import { useUniqueId } from '../hooks/useUniqueId';
import { compose, composeU } from '../lib/compose';
import { withFullWidth } from './_fullWidth/RadioButton_fullWidth';
import { withSizeL } from './_size/RadioButton_size_l';
import { withSizeS } from './_size/RadioButton_size_s';
import { withViewContrast } from './_view/RadioButton_view_contrast';
import { RadioButtonContent as Content } from './Content/RadioButton-Content';
import { RadioButtonControl as Control } from './Control/RadioButton-Control';
import { RadioButtonPlate as Plate } from './Plate/RadioButton-Plate';
import { RadioButtonRadio as Radio } from './Radio/RadioButton-Radio';
import { cnRadioButton } from './RadioButton.cn';
import type { RadioButtonBaseProps } from './RadioButton.types';

import './RadioButton.css';

const enhance = compose(composeU(withViewContrast), composeU(withSizeS, withSizeL), withFullWidth);

/** Компонент для создания радиогруппы */
export const RadioButton = enhance(
    ({
        className,
        style,
        innerRef,
        'aria-label': ariaLabel,
        options,
        'value': rootValue,
        'disabled': rootDisabled,
        // eslint-disable-next-line react-hooks/rules-of-hooks
        name = useUniqueId(),
        size = 'm',
        view = 'default',
        ...props
    }: RadioButtonBaseProps) => {
        const plateRef = useRef<HTMLDivElement>(null);
        const radioRef = useRef<HTMLLabelElement>();
        const radioCheckedRef = useCallback((radioCheckedNode: HTMLLabelElement) => {
            if (!radioCheckedNode) return;

            const plateNode = plateRef.current;

            if (!plateNode) return;

            const radioUncheckedNode = radioRef.current;

            // Показываем Plate только во время перехода между двумя Radio
            if (radioUncheckedNode && radioUncheckedNode !== radioCheckedNode) {
                const setPlateStyle = (node: HTMLElement) => {
                    plateNode.style.left = `${node.offsetLeft}px`;
                    plateNode.style.width = `${node.offsetWidth}px`;
                };

                setPlateStyle(radioUncheckedNode);

                plateNode.hidden = false;

                setPlateStyle(radioCheckedNode);
            }

            radioRef.current = radioCheckedNode;
        }, []);

        return (
            <span
                className={cnRadioButton({ size, view, disabled: rootDisabled }, [className])}
                style={style}
                ref={innerRef}
                role="radiogroup"
                aria-label={ariaLabel}
                onFocus={React.useCallback<React.FocusEventHandler>(
                    (event) => event.currentTarget.setAttribute('data-focused', ''),
                    [],
                )}
                onBlur={React.useCallback<React.FocusEventHandler>(
                    (event) => event.currentTarget.removeAttribute('data-focused'),
                    [],
                )}
                onKeyDown={React.useCallback<React.KeyboardEventHandler>(
                    (event) => event.repeat && event.preventDefault(),
                    [],
                )}
            >
                <Plate
                    innerRef={plateRef}
                    onTransitionEnd={React.useCallback<
                        React.TransitionEventHandler<HTMLDivElement>
                    >((event) => (event.currentTarget.hidden = true), [])}
                />
                {options.map(({ children, ...controlProps }) => {
                    const checked = rootValue === controlProps.value;

                    controlProps.disabled = rootDisabled || controlProps.disabled;

                    return (
                        <Radio
                            checked={checked}
                            disabled={controlProps.disabled}
                            key={controlProps.value}
                            innerRef={checked ? radioCheckedRef : undefined}
                        >
                            <Control {...props} {...controlProps} checked={checked} name={name} />
                            <Content>{children}</Content>
                        </Radio>
                    );
                })}
            </span>
        );
    },
);

RadioButton.displayName = cnRadioButton();

export type RadioButtonProps = React.ComponentProps<typeof RadioButton>;
